// 🔥 TEMPORARILY BYPASS FIREBASE FOR TESTING - ENHANCED COMPANY CONTEXT
// TODO: Restore Firebase functionality when authentication is ready

import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Capacitor } from '@capacitor/core';
import { CompanyManager, EmployeeManager } from '../utils/enhancedFirebaseUtils';
import { auth, db, setGlobalCompanyId, stopTracking } from '../firebase.config';
import { doc, setDoc, getDoc, onSnapshot, collection } from '../firebase.config';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
// 🔒 PHASE 1: Import secure cache manager for cache clearing
import { secureCacheManager, clearCacheOnLogout } from '../utils/secureCacheManager';
// 🔄 Import provision request handler for new user signup
import { processProvisionRequest, getUserProfileData } from '../utils/provisionRequestHandler';

// 🔐 Context for enhanced company management
const EnhancedCompanyContext = createContext();

// 🎯 Action types for company context
const ACTIONS = {
  SET_CURRENT_COMPANY: 'SET_CURRENT_COMPANY',
  SET_COMPANY_INFO: 'SET_COMPANY_INFO',
  SET_EMPLOYEES: 'SET_EMPLOYEES',
  SET_USER_COMPANIES: 'SET_USER_COMPANIES',
  SET_MPIN_VERIFIED: 'SET_MPIN_VERIFIED',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_COMPANY_DATA: 'CLEAR_COMPANY_DATA',
  UPDATE_EMPLOYEE: 'UPDATE_EMPLOYEE',
  ADD_EMPLOYEE: 'ADD_EMPLOYEE',
  REMOVE_EMPLOYEE: 'REMOVE_EMPLOYEE',
  SET_USER_ROLE: 'SET_USER_ROLE',
  SET_PERMISSIONS: 'SET_PERMISSIONS'
};

// 🏗️ Initial state for company context
const initialState = {
  currentCompany: null,
  companyInfo: null,
  plan: 'basic',
  features: {},
  employees: [],
  userCompanies: [],
  mpinVerified: false,
  loading: false,
  error: null,
  userRole: null,
  permissions: []
};

// 🔄 Reducer for company context
const companyReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_CURRENT_COMPANY:
      return { ...state, currentCompany: action.payload, mpinVerified: false };
    case ACTIONS.SET_COMPANY_INFO:
      return { ...state, companyInfo: action.payload };
    case ACTIONS.SET_EMPLOYEES:
      return { ...state, employees: action.payload };
    case ACTIONS.SET_USER_COMPANIES:
      return { ...state, userCompanies: action.payload };
    case ACTIONS.SET_MPIN_VERIFIED:
      return { ...state, mpinVerified: action.payload };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ACTIONS.CLEAR_COMPANY_DATA:
      return { ...initialState };
    case ACTIONS.UPDATE_EMPLOYEE:
      return { ...state, employees: state.employees.map(e => (e.id === action.payload.id ? { ...e, ...action.payload } : e)) };
    case ACTIONS.ADD_EMPLOYEE:
      return { ...state, employees: [...state.employees, action.payload] };
    case ACTIONS.REMOVE_EMPLOYEE:
      return { ...state, employees: state.employees.filter(e => e.id !== action.payload) };
    case ACTIONS.SET_USER_ROLE:
      return { ...state, userRole: action.payload };
    case ACTIONS.SET_PERMISSIONS:
      return { ...state, permissions: action.payload };
    default:
      return state;
  }
};

// 🏢 Enhanced Company Provider Component
export const EnhancedCompanyProvider = ({ children, user, forceReload = false }) => {
  const [state, dispatch] = useReducer(companyReducer, initialState);
  const [showWizardForFirstTime, setShowWizardForFirstTime] = useState(false);
  const isLoadingRef = useRef(false); // Track loading state to prevent multiple simultaneous loads
  const companiesLoadAttemptedRef = useRef(false); // True only after first fetch completes (success or fail)

  // Try to infer existing companies from membership if user_companies doc is missing/empty
  const inferCompaniesFromMembership = async (uid) => {
    try {
      const { collectionGroup, query, getDocs, db } = await import('../firebase.config');
      // Query all employee membership docs and filter client-side for robustness across legacy schemas
      const q = query(collectionGroup(db, 'employees'));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((docSnap) => {
        // Path shape: companies/{companyId}/employees/{uid}
        const segments = docSnap.ref.path.split('/');
        const companyIdIndex = segments.indexOf('companies') + 1;
        const companyId = segments[companyIdIndex];
        const data = docSnap.data() || {};
        const isSameUser = docSnap.id === uid || data.user_id === uid || data.uid === uid || data.userId === uid;
        if (!isSameUser) return;
        const role = data.role || 'employee';
        if (companyId) results.push({ companyId, type: role === 'owner' ? 'owned' : 'employed' });
      });
      return results;
    } catch (_) {
      return [];
    }
  };

  // Remove legacy detection; always create/switch using new structure only
  const detectLegacyCompanyFromArtifacts = async (_uid) => ({ exists: false });

  // Only show wizard on first time - check localStorage to prevent auto-opening
  const maybeOpenWizardOnce = (uid) => {
    const hasSeenWizard = localStorage.getItem(`acctoo_wizard_seen_${uid}`);
    if (!hasSeenWizard) {
      setShowWizardForFirstTime(true);
      localStorage.setItem(`acctoo_wizard_seen_${uid}`, 'true');
    }
  };

  // Function to manually open wizard (for button press)
  const openWizardManually = () => {
    setShowWizardForFirstTime(true);
  };

  // Migrate existing companies where user is owner
  const migrateExistingCompanies = async (userId) => {
    try {
      console.log('🔄 Migrating existing companies for user:', userId);
      const { getDocs, query, where, collection, db } = await import('../firebase.config');
      
      // Find companies where this user is the owner
      const companiesQuery = query(collection(db, 'companies'), where('owner.userId', '==', userId));
      const companiesSnap = await getDocs(companiesQuery);
      
      if (!companiesSnap.empty) {
        const ownedCompanies = [];
        companiesSnap.forEach(docSnap => {
          ownedCompanies.push({
            companyId: docSnap.id,
            role: 'owner',
            status: 'active',
            addedAt: new Date()
          });
        });
        
        console.log('🔄 Found existing companies to migrate:', ownedCompanies);
        
        // Create user_companies document with all found companies
        const userCompaniesRef = doc(db, 'user_companies', userId);
        
        // Check if document already exists to merge with existing data
        const existingDoc = await getDoc(userCompaniesRef);
        const existingData = existingDoc.exists() ? existingDoc.data() : {};
        const existingOwned = existingData.owned_companies || [];
        
        // Merge new companies with existing ones (avoid duplicates)
        const allOwnedCompanies = [...existingOwned];
        ownedCompanies.forEach(newCompany => {
          const exists = allOwnedCompanies.find(existing => existing.companyId === newCompany.companyId);
          if (!exists) {
            allOwnedCompanies.push(newCompany);
          }
        });
        
        await setDoc(userCompaniesRef, {
          owned_companies: allOwnedCompanies,
          employed_companies: existingData.employed_companies || [],
          active_company: existingData.active_company || {
            companyId: allOwnedCompanies[0].companyId,
            type: 'owned',
            switched_at: new Date()
          },
          last_updated: new Date()
        });
        
        console.log('✅ User companies document created, reloading...');
        // Reload companies after migration
        await loadUserCompanies(userId);
      } else {
        console.log('📋 No existing companies found, showing wizard');
        // Explicitly set currentCompany to null when no companies found
        dispatch({ type: ACTIONS.SET_CURRENT_COMPANY, payload: null });
        maybeOpenWizardOnce(userId);
      }
    } catch (e) {
      console.error('🔄 Migration error:', e);
      maybeOpenWizardOnce(userId);
    }
  };

  // Load user's companies from database
  const loadUserCompanies = useCallback(async (userId) => {
    // Prevent multiple simultaneous loads
    if (isLoadingRef.current) {
      return;
    }
    
    // Additional check to prevent rapid successive calls
    const lastLoadTime = sessionStorage.getItem('lastCompanyLoadTime');
    const now = Date.now();
    if (lastLoadTime && (now - parseInt(lastLoadTime)) < 2000) {
      return;
    }
    sessionStorage.setItem('lastCompanyLoadTime', now.toString());
    
    try {
      isLoadingRef.current = true;
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      
      // Fetch user's company associations
      const userCompaniesResult = await CompanyManager.getUserCompanies(userId);
      if (userCompaniesResult.success) {
        const userData = userCompaniesResult.data;

        // Build a unified, de-duplicated list of company references from both
        // legacy arrays and the new `companies` array. The new structure takes
        // precedence when overlapping entries exist.
        const refsById = new Map();

        const normalizeRef = (ref) => ({
          companyId: ref.companyId,
          role: ref.role || (ref.type === 'owned' ? 'owner' : 'employee') || 'employee',
          status: ref.status || 'active',
          addedAt: ref.addedAt || ref.joinedAt || new Date()
        });

        const upsert = (ref) => {
          if (!ref || !ref.companyId) return;
          const normalized = normalizeRef(ref);
          const existing = refsById.get(normalized.companyId);
          if (!existing) {
            refsById.set(normalized.companyId, normalized);
            return;
          }
          // Prefer owner role if any entry marks it as owner
          const preferredRole = existing.role === 'owner' || normalized.role === 'owner' ? 'owner' : (existing.role || normalized.role);
          refsById.set(normalized.companyId, {
            companyId: normalized.companyId,
            role: preferredRole,
            status: normalized.status || existing.status || 'active',
            addedAt: existing.addedAt || normalized.addedAt
          });
        };

        // 1) Legacy arrays
        (userData.owned_companies || []).forEach((c) => upsert({ ...c, role: 'owner' }));
        (userData.employed_companies || []).forEach((c) => upsert({ ...c, role: 'employee' }));

        // 2) New unified array
        if (Array.isArray(userData.companies)) {
          userData.companies.forEach((c) => upsert(c));
        }

        const allCompanyRefs = Array.from(refsById.values());
        
        // Fetch company details — parallel on native for faster cold start; sequential on web
        const mapCompanyDetail = async (companyRef) => {
          try {
            const companyInfo = await CompanyManager.getCompanyInfo(companyRef.companyId);
            if (companyInfo.success) {
              return {
                companyId: companyRef.companyId,
                role: companyRef.role,
                status: companyRef.status || 'active',
                addedAt: companyRef.addedAt,
                ...companyInfo.data
              };
            }
            console.warn('Failed to get company info:', companyRef.companyId, companyInfo.error);
            return {
              companyId: companyRef.companyId,
              role: companyRef.role,
              status: companyRef.status || 'active',
              addedAt: companyRef.addedAt,
              name: `Company ${companyRef.companyId}`,
              details: { firmName: `Company ${companyRef.companyId}` }
            };
          } catch (e) {
            console.warn('Exception loading company details:', companyRef.companyId, e);
            return {
              companyId: companyRef.companyId,
              role: companyRef.role,
              status: companyRef.status || 'active',
              addedAt: companyRef.addedAt,
              name: `Company ${companyRef.companyId}`,
              details: { firmName: `Company ${companyRef.companyId}` }
            };
          }
        };

        let companiesWithDetails = [];
        if (Capacitor.isNativePlatform()) {
          companiesWithDetails = await Promise.all(allCompanyRefs.map(mapCompanyDetail));
        } else {
          for (const companyRef of allCompanyRefs) {
            companiesWithDetails.push(await mapCompanyDetail(companyRef));
          }
        }
        dispatch({ type: ACTIONS.SET_USER_COMPANIES, payload: companiesWithDetails });
        
        // Set active company from userData or first available
        const activeCompanyId = userData.active_company?.companyId;
        if (activeCompanyId) {
          const activeCompany = companiesWithDetails.find(c => c.companyId === activeCompanyId);
          if (activeCompany) {
            dispatch({ type: ACTIONS.SET_CURRENT_COMPANY, payload: activeCompany });
            await loadCompanyInfo(activeCompanyId);
            await loadCompanyEmployees(activeCompanyId);
            
            // Store in localStorage for faster access
            localStorage.setItem('acctoo_last_company', activeCompanyId);
          } else {
            if (companiesWithDetails.length > 0) {
              const firstCompany = companiesWithDetails[0];
              dispatch({ type: ACTIONS.SET_CURRENT_COMPANY, payload: firstCompany });
              await loadCompanyInfo(firstCompany.companyId);
              await loadCompanyEmployees(firstCompany.companyId);
            }
          }
        } else if (companiesWithDetails.length > 0) {
          // No active company set in database, use first available and update database
          const firstCompany = companiesWithDetails[0];
          dispatch({ type: ACTIONS.SET_CURRENT_COMPANY, payload: firstCompany });
          
          // Update database with new active company
          const { db } = await import('../firebase.config');
          const userCompaniesRef = doc(db, 'user_companies', userId);
          await setDoc(userCompaniesRef, {
            ...userData,
            active_company: {
              companyId: firstCompany.companyId,
              type: firstCompany.role === 'owner' ? 'owned' : 'employed',
              switched_at: new Date()
            },
            last_updated: new Date()
          }, { merge: true });
          
          await loadCompanyInfo(firstCompany.companyId);
          await loadCompanyEmployees(firstCompany.companyId);
          localStorage.setItem('acctoo_last_company', firstCompany.companyId);
        } else {
          // User has no companies - explicitly set currentCompany to null
          // This allows dashboard to detect this state and show wizard
          console.log('📋 User has no companies, setting currentCompany to null');
          dispatch({ type: ACTIONS.SET_CURRENT_COMPANY, payload: null });
          // Show wizard for first-time users
          maybeOpenWizardOnce(userId);
        }
        
        // Return companies for use in createCompany
        return companiesWithDetails;
      } else {
        // Try to find existing companies where user is owner
        await migrateExistingCompanies(userId);
        return [];
      }
    } catch (e) {
      console.error('Error loading user companies:', e);
      dispatch({ type: ACTIONS.SET_ERROR, payload: e.message });
      
      // Retry once after a short delay
      setTimeout(() => {
        loadUserCompanies(userId);
      }, 2000);
      
      maybeOpenWizardOnce(userId);
      return [];
    } finally {
      isLoadingRef.current = false; // Reset loading flag
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
      companiesLoadAttemptedRef.current = true; // Mark that we've completed at least one fetch
    }
  }, [dispatch]); // Add dispatch as dependency

  const loadCompanyInfo = async (companyId) => {
    if (!companyId) return;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const info = await CompanyManager.getCompanyInfo(companyId);
      
      if (info.success) {
        const companyData = { id: companyId, ...info.data };
        dispatch({ type: ACTIONS.SET_COMPANY_INFO, payload: companyData });
      } else {
        console.error('❌ Failed to load company info:', info.error);
      }
    } catch (e) {
      console.error('❌ Error in loadCompanyInfo:', e);
      dispatch({ type: ACTIONS.SET_ERROR, payload: e.message });
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Store unsubscribe function for employees listener
  const employeesUnsubscribeRef = useRef(null);
  const companyDocUnsubscribeRef = useRef(null);

  // Realtime company profile — keeps companyInfo (and derived profile fields) in sync app-wide
  useEffect(() => {
    const companyId = state.currentCompany?.companyId || state.currentCompany?.id;
    if (companyDocUnsubscribeRef.current) {
      companyDocUnsubscribeRef.current();
      companyDocUnsubscribeRef.current = null;
    }
    if (!db || !companyId) return;

    const unsub = onSnapshot(
      doc(db, 'companies', companyId),
      (snap) => {
        if (!snap.exists()) return;
        const normalized = normalizeCompanyDocSnapshot(snap.data() || {}, companyId);
        dispatch({ type: ACTIONS.SET_COMPANY_INFO, payload: { id: companyId, ...normalized } });
      },
      (error) => console.error('Company profile realtime listener error:', error)
    );
    companyDocUnsubscribeRef.current = unsub;
    return () => {
      unsub();
      companyDocUnsubscribeRef.current = null;
    };
  }, [state.currentCompany?.companyId, state.currentCompany?.id]);

  const activeCompanyProfile = useMemo(() => {
    if (!state.companyInfo && !state.currentCompany) return null;
    return { ...(state.currentCompany || {}), ...(state.companyInfo || {}) };
  }, [state.currentCompany, state.companyInfo]);

  const loadCompanyEmployees = useCallback((companyId) => {
    if (!companyId) return;
    
    // Clean up previous listener if exists
    if (employeesUnsubscribeRef.current) {
      console.log('🧹 Cleaning up previous employees listener');
      employeesUnsubscribeRef.current();
      employeesUnsubscribeRef.current = null;
    }
    
    try {
      const employeesRef = getCollectionForCompany(db, { companyId }, 'employees');
      
      // Validate collection reference before setting up listener
      if (!employeesRef) {
        console.warn('⚠️ Invalid employees collection reference, falling back to one-time fetch');
        EmployeeManager.getCompanyEmployees(companyId).then((res) => {
          if (res.success) {
            dispatch({ type: ACTIONS.SET_EMPLOYEES, payload: res.data });
          }
        });
        return;
      }
      
      // Create real-time listener with better error handling
      const unsubscribe = onSnapshot(
        employeesRef,
        (snapshot) => {
          const employees = [];
          snapshot.forEach((doc) => {
            // Spread first so stored `id` in document data cannot overwrite Firestore doc id (fixes duplicate React keys)
            employees.push({ ...doc.data(), id: doc.id });
          });
          
          dispatch({ type: ACTIONS.SET_EMPLOYEES, payload: employees });
        },
        (error) => {
          console.error('❌ Error in employees real-time listener:', error);
          
          // Handle specific error codes
          if (error.code === 'permission-denied') {
            console.warn('⚠️ Permission denied for employees listener - user may not be verified');
          } else if (error.code === 'unavailable') {
            console.warn('⚠️ Firestore unavailable - will retry on next load');
          } else if (error.message?.includes('Bad Request') || error.code === 'invalid-argument') {
            console.error('❌ Invalid Firestore query - check collection path and permissions');
          }
          
          // Fallback to one-time fetch on error
          EmployeeManager.getCompanyEmployees(companyId).then((res) => {
            if (res.success) {
              dispatch({ type: ACTIONS.SET_EMPLOYEES, payload: res.data });
            }
          }).catch((fallbackError) => {
            console.error('❌ Fallback fetch also failed:', fallbackError);
          });
        }
      );
      
      // Store unsubscribe function for cleanup
      employeesUnsubscribeRef.current = unsubscribe;
      console.log('👂 Real-time employees listener set up for company:', companyId);
    } catch (error) {
      console.error('Error setting up employees listener:', error);
      
      // Handle specific error types
      if (error.message?.includes('Bad Request') || error.code === 'invalid-argument') {
        console.error('❌ Invalid Firestore path or query parameters');
      }
      
      // Fallback to one-time fetch
      EmployeeManager.getCompanyEmployees(companyId).then((res) => {
        if (res.success) {
          dispatch({ type: ACTIONS.SET_EMPLOYEES, payload: res.data });
        }
      }).catch((fallbackError) => {
        console.error('❌ Fallback fetch also failed:', fallbackError);
      });
    }
  }, []);

  const switchCompany = async (companyId, companiesOverride = null) => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid || !companyId) {
        console.error('❌ Missing user ID or company ID for switch');
        return;
      }
      
      // Use provided companies or fall back to state
      const companiesToSearch = companiesOverride || state.userCompanies;
      
      // 🔒 PHASE 1: Clear cache for previous company before switching
      const previousCompanyId = state.currentCompany?.companyId;
      if (previousCompanyId && previousCompanyId !== companyId) {
        secureCacheManager.invalidatePattern(`.*_${previousCompanyId}`);
      }
      
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      
      // Find the company in user's companies list (use provided companies or state)
      const targetCompany = companiesToSearch.find(c => c.companyId === companyId);
      
      if (!targetCompany) {
        console.error('❌ Company not found in user companies list');
        throw new Error('Company not found in user companies');
      }
      
      // Validate user has access to the company
      const hasAccess = CompanyManager.checkUserCompanyAccess(
        { 
          owned_companies: companiesToSearch.filter(c => c.role === 'owner'),
          employed_companies: companiesToSearch.filter(c => c.role !== 'owner'),
          companies: companiesToSearch
        }, 
        companyId
      );
      
      if (!hasAccess) {
        console.error('❌ User does not have access to company:', companyId);
        throw new Error('Access denied to company');
      }
      
      // Update user's active company in database with enhanced mapping
      const switchResult = await CompanyManager.switchCompany(uid, companyId, targetCompany.role === 'owner' ? 'owned' : 'employed');
      
      if (!switchResult.success) {
        throw new Error(switchResult.error || 'Failed to switch company');
      }
      
      // Update local state with enhanced company data
      const enhancedTargetCompany = {
        ...targetCompany,
        // Add switch metadata
        switch_timestamp: new Date().toISOString(),
        previous_company: state.currentCompany?.companyId || null,
        switch_success: true
      };
      
      dispatch({ type: ACTIONS.SET_CURRENT_COMPANY, payload: enhancedTargetCompany });
      
      // Load company details and employees
      await loadCompanyInfo(companyId);
      await loadCompanyEmployees(companyId);
      
      // Store in localStorage for persistence across sessions
      const persistenceData = {
        companyId,
        switch_timestamp: new Date().toISOString(),
        user_id: uid,
        company_name: targetCompany.name || targetCompany.details?.firmName || `Company ${companyId}`,
        role: targetCompany.role
      };
      
      localStorage.setItem('acctoo_last_company', companyId);
      localStorage.setItem('acctoo_company_switch_data', JSON.stringify(persistenceData));
      
      // Trigger custom event for other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('companySwitched', {
          detail: {
            companyId,
            company: enhancedTargetCompany,
            previousCompany: state.currentCompany,
            userId: uid,
            timestamp: new Date().toISOString()
          }
        }));
      }
      
    } catch (e) {
      console.error('❌ Error switching company:', e);
      dispatch({ type: ACTIONS.SET_ERROR, payload: e.message });
      
      // Trigger error event for other components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('companySwitchError', {
          detail: {
            companyId,
            error: e.message,
            userId: auth.currentUser?.uid,
            timestamp: new Date().toISOString()
          }
        }));
      }
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  const createCompany = async (companyInfo, security) => {
    try {
      const uid = auth.currentUser?.uid;
      if (!uid) return { success: false, error: 'Not authenticated' };
      const res = await CompanyManager.createCompany(uid, companyInfo, security);
      if (res.success) {
        // Clear the "creating company" flag since company was successfully created
        sessionStorage.removeItem(`acctoo_creating_company_${uid}`);
        
        // Reload user companies to get the new company and get the companies array
        const updatedCompanies = await loadUserCompanies(uid);
        
        // Try to switch to the new company using the returned companies (avoiding state race condition)
        try {
          if (updatedCompanies && updatedCompanies.length > 0) {
            await switchCompany(res.companyId, updatedCompanies);
          } else {
            // Fallback: wait a bit longer and use state
            await new Promise(resolve => setTimeout(resolve, 300));
            await switchCompany(res.companyId);
          }
        } catch (switchError) {
          console.warn('Could not auto-switch to new company:', switchError.message);
          // Return success anyway since company was created
        }
        
        return { success: true, companyId: res.companyId };
      }
      return res;
    } catch (e) {
      console.error('❌ Error in createCompany:', e);
      // Clear the flag even on error so user can retry
      const uid = auth.currentUser?.uid;
      if (uid) {
        sessionStorage.removeItem(`acctoo_creating_company_${uid}`);
      }
      return { success: false, error: e.message };
    }
  };

  // One-time auto-migration function (call manually if needed)
  const runAutoMigration = async (companyId) => {
    try {
      const result = await EmployeeManager.autoMigrateOldEmployees(companyId);
      if (result.success) {
        // Reload employees after migration
        await loadCompanyEmployees(companyId);
        return result;
      }
      return result;
    } catch (error) {
      console.error('❌ Auto-migration failed:', error);
      return { success: false, error: error.message };
    }
  };

  // Reset companies load flag when user logs out
  useEffect(() => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid) {
      companiesLoadAttemptedRef.current = false;
    }
  }, [user?.uid]);

  // Initial load when user authenticates
  useEffect(() => {
    const uid = user?.uid || auth.currentUser?.uid;
    if (!uid) return;
    // Set loading true immediately so FirstTimeCompanyWizard waits for data before deciding to show
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    // Process provision request first (for new user signups)
    const initializeUser = async () => {
      try {
        console.log('🔍 Checking for provision request for new user:', uid);
        
        // Get user profile data for provision processing
        const userProfileData = await getUserProfileData(uid);
        const userAuthData = {
          email: user?.email || auth.currentUser?.email || userProfileData?.email || '',
          displayName: user?.displayName || auth.currentUser?.displayName || userProfileData?.displayName || '',
          phone: user?.phoneNumber || auth.currentUser?.phoneNumber || userProfileData?.phone || '',
          emailVerified: user?.emailVerified || auth.currentUser?.emailVerified || userProfileData?.emailVerified || false,
          ...userProfileData
        };
        
        // Process provision request if exists
        const provisionResult = await processProvisionRequest(uid, userAuthData);
        
        if (provisionResult.success && provisionResult.processed && provisionResult.companyId) {
          console.log('✅ Provision request processed, company created:', provisionResult.companyId);
          // Web: small delay for consistency; native: skip artificial wait
          if (!Capacitor.isNativePlatform()) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }
        
        // Always load user companies (whether provision request was processed or not)
        await loadUserCompanies(uid);
      } catch (error) {
        console.error('❌ Error initializing user:', error);
        // Continue with loading user companies even if provision request fails
        try {
          await loadUserCompanies(uid);
        } catch (loadError) {
          console.error('❌ Error loading user companies:', loadError);
          dispatch({ type: ACTIONS.SET_LOADING, payload: false });
        }
      }
    };
    
    // Web: small delay for mount; native: start immediately
    const initDelayMs = Capacitor.isNativePlatform() ? 0 : 100;
    const timeoutId = setTimeout(() => {
      initializeUser();
    }, initDelayMs);
    
    return () => clearTimeout(timeoutId);
  }, [user, auth.currentUser]);

  // Listen for user authentication events (for persistent login)
  useEffect(() => {
    const handleUserAuthenticated = (event) => {
      const { user: userData } = event.detail;
      if (userData?.uid) {
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        loadUserCompanies(userData.uid);
      }
    };

    const handleAppRefresh = (event) => {
      console.log('🔄 App refresh event received, reloading company data...');
      const uid = user?.uid || auth.currentUser?.uid;
      if (uid) {
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        loadUserCompanies(uid);
      }
    };

    // Add event listeners
    window.addEventListener('userAuthenticated', handleUserAuthenticated);
    window.addEventListener('appRefresh', handleAppRefresh);
    
    return () => {
      window.removeEventListener('userAuthenticated', handleUserAuthenticated);
      window.removeEventListener('appRefresh', handleAppRefresh);
    };
  }, [user?.uid, loadUserCompanies]); // Use user?.uid instead of user

  // Additional effect to ensure company loading on persistent login
  useEffect(() => {
    const checkAndLoadCompanies = () => {
      const uid = user?.uid || auth.currentUser?.uid;
      if (uid && !state.userCompanies.length && !state.loading && !isLoadingRef.current) {
        console.log('🔄 Ensuring company data is loaded for persistent login...');
        loadUserCompanies(uid);
      }
    };

    // Native: no artificial delay; web keeps short delay for timing edge cases
    const ensureDelayMs = Capacitor.isNativePlatform() ? 0 : 500;
    const timeoutId = setTimeout(checkAndLoadCompanies, ensureDelayMs);
    
    return () => clearTimeout(timeoutId);
  }, [user?.uid, state.userCompanies.length, state.loading, loadUserCompanies]); // Use user?.uid instead of user

  // Force reload effect when forceReload prop changes
  useEffect(() => {
    if (forceReload && user?.uid) {
      console.log('🔄 Force reload triggered, loading company data...');
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      loadUserCompanies(user.uid);
    }
  }, [forceReload, user?.uid]);

  // Quick load last used company from localStorage as fallback
  useEffect(() => {
    const quickLoadLastCompany = () => {
      try {
        const lastCompanyId = localStorage.getItem('acctoo_last_company');
        if (lastCompanyId && !state.currentCompany) {
          console.log('🚀 Quick loading last used company from localStorage:', lastCompanyId);
          // Try to load company info immediately
          loadCompanyInfo(lastCompanyId).then(() => {
            console.log('✅ Last company loaded successfully');
          }).catch(error => {
            console.log('⚠️ Could not load last company, will wait for full user data load');
          });
        }
      } catch (error) {
        console.error('Error in quick company load:', error);
      }
    };

    // Native: one pass only; web also retries after 500ms
    quickLoadLastCompany();
    if (Capacitor.isNativePlatform()) {
      return undefined;
    }
    const timeoutId = setTimeout(quickLoadLastCompany, 500);
    
    return () => clearTimeout(timeoutId);
  }, [state.currentCompany]);

  // Keep Firebase operations tracking synced with selected company globally.
  // This prevents missed tracking when user works in screens that don't call setGlobalCompanyId.
  useEffect(() => {
    const activeCompanyId =
      state.currentCompany?.companyId ||
      state.currentCompany?.id ||
      state.companyInfo?.companyId ||
      state.companyInfo?.id ||
      null;

    if (activeCompanyId) {
      setGlobalCompanyId(activeCompanyId);
      try {
        localStorage.setItem('acctoo_last_company', activeCompanyId);
      } catch (_) {
        // Ignore storage failures
      }
    } else {
      stopTracking();
    }
  }, [
    state.currentCompany?.companyId,
    state.currentCompany?.id,
    state.companyInfo?.companyId,
    state.companyInfo?.id
  ]);

  // Listen for user companies updates (when employee is added to company)
  useEffect(() => {
    const handleUserCompaniesUpdate = (event) => {
      const { userId } = event.detail;
      const currentUserId = auth.currentUser?.uid;
      
      if (userId === currentUserId) {
        console.log('🔄 User companies updated, reloading...');
        // Add delay to ensure database write is complete
        setTimeout(() => {
          loadUserCompanies(currentUserId);
        }, 1000);
      }
    };

    window.addEventListener('userCompaniesUpdated', handleUserCompaniesUpdate);
    
    return () => {
      window.removeEventListener('userCompaniesUpdated', handleUserCompaniesUpdate);
    };
  }, []);

  // Listen for user restored from persistent session
  useEffect(() => {
    const handleUserRestoredFromSession = (event) => {
      const { user: restoredUser, appId } = event.detail;
      console.log('🔄 User restored from persistent session, loading company data for:', restoredUser.uid);
      
      // Immediately load user companies for the restored user
      if (restoredUser && restoredUser.uid) {
        console.log('🚀 Triggering immediate company data load for restored user');
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        loadUserCompanies(restoredUser.uid);
      }
    };

    const handleCompanyDataLoad = (event) => {
      const { user: userData, appId } = event.detail;
      console.log('🔄 Company data load event received for:', userData?.uid);
      
      if (userData && userData.uid) {
        console.log('🚀 Triggering company data load from event');
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        loadUserCompanies(userData.uid);
      }
    };

    const handleForceRefresh = (event) => {
      const { user: userData, appId } = event.detail;
      console.log('🔄 Force refresh event received for:', userData?.uid);
      
      if (userData && userData.uid) {
        console.log('🚀 Triggering force refresh company data load');
        dispatch({ type: ACTIONS.SET_LOADING, payload: true });
        loadUserCompanies(userData.uid);
      }
    };

    window.addEventListener('userRestoredFromSession', handleUserRestoredFromSession);
    window.addEventListener('companyDataLoad', handleCompanyDataLoad);
    window.addEventListener('forceRefresh', handleForceRefresh);
    
    return () => {
      window.removeEventListener('userRestoredFromSession', handleUserRestoredFromSession);
      window.removeEventListener('companyDataLoad', handleCompanyDataLoad);
      window.removeEventListener('forceRefresh', handleForceRefresh);
    };
  }, [loadUserCompanies]);

  // Auto-refresh user companies every 30 seconds to catch updates - DISABLED
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const currentUserId = auth.currentUser?.uid;
  //     if (currentUserId) {
  //       console.log('🔄 Auto-refreshing user companies...');
  //       loadUserCompanies(currentUserId);
  //     }
  //   }, 30000);

  //   return () => clearInterval(interval);
  // }, []); // Remove loadUserCompanies dependency to prevent infinite loop

  // Load employees when current company changes (with real-time listener)
  useEffect(() => {
    if (state.currentCompany?.companyId) {
      console.log('🔄 Setting up real-time employees listener for company:', state.currentCompany.companyId);
      loadCompanyEmployees(state.currentCompany.companyId);
    }
    
    // Cleanup: unsubscribe when company changes or component unmounts
    return () => {
      if (employeesUnsubscribeRef.current) {
        console.log('🧹 Cleaning up employees listener on unmount/company change');
        employeesUnsubscribeRef.current();
        employeesUnsubscribeRef.current = null;
      }
    };
  }, [state.currentCompany?.companyId, loadCompanyEmployees]);

  // Employee Management Functions
  const createEmployee = async (employeeData) => {
    if (!state.currentCompany?.companyId) return { success: false, error: 'No company selected' };
    
    const result = await EmployeeManager.createEmployee(state.currentCompany.companyId, employeeData);
    
    if (result.success) {
      // Add the new employee to the state (doc id must win over any `id` field in stored data)
      dispatch({ type: ACTIONS.ADD_EMPLOYEE, payload: { ...result.data, id: result.employeeId } });
      // Reload employees to ensure consistency
      await loadCompanyEmployees(state.currentCompany.companyId);
    }
    
    return result;
  };

  const updateEmployee = async (employeeId, updateData) => {
    if (!state.currentCompany?.companyId) return { success: false, error: 'No company selected' };
    
    const result = await EmployeeManager.updateEmployee(state.currentCompany.companyId, employeeId, updateData);
    
    if (result.success) {
      // Update the employee in the state
      dispatch({ type: ACTIONS.UPDATE_EMPLOYEE, payload: { ...updateData, id: employeeId } });
      // Reload employees to ensure consistency
      await loadCompanyEmployees(state.currentCompany.companyId);
    }
    
    return result;
  };

  const deleteEmployee = async (employeeId) => {
    if (!state.currentCompany?.companyId) return { success: false, error: 'No company selected' };
    
    const result = await EmployeeManager.deleteEmployee(state.currentCompany.companyId, employeeId);
    
    if (result.success) {
      // Remove the employee from the state
      dispatch({ type: ACTIONS.REMOVE_EMPLOYEE, payload: employeeId });
    }
    
    return result;
  };

  const getEmployee = async (employeeId) => {
    if (!state.currentCompany?.companyId) return { success: false, error: 'No company selected' };
    return await EmployeeManager.getEmployee(state.currentCompany.companyId, employeeId);
  };

  // Permission checking functions
  const hasPermission = (page, operation = 'view') => {
    const uid = auth.currentUser?.uid;
    if (!uid || !state.currentCompany?.companyId) return false;
    
    // For now, assume current user has permissions (this will be enhanced later)
    return true;
  };

  const canAccessPage = (page) => {
    const uid = auth.currentUser?.uid;
    if (!uid || !state.currentCompany?.companyId) return false;
    
    // For now, assume current user can access pages (this will be enhanced later)
    return true;
  };

  const isCurrentUserOwner = () => {
    const uid = auth.currentUser?.uid;
    if (!uid || !state.currentCompany?.companyId) return false;
    
    // Check if current user is owner of the current company
    const currentCompanyRef = state.userCompanies.find(
      company => company.companyId === state.currentCompany?.companyId
    );
    
    return currentCompanyRef?.role === 'owner';
  };

  const contextValue = {
    state,
    currentCompany: state.currentCompany,
    companyInfo: state.companyInfo,
    activeCompanyProfile,
    userCompanies: state.userCompanies,
    employees: state.employees,
    loading: state.loading,
    error: state.error,
    switchCompany,
    createCompany,
    loadUserCompanies,
    loadCompanyEmployees,
    loadCompanyInfo,
    showWizardForFirstTime,
    setShowWizardForFirstTime,
    openWizardManually,
    
    // Employee Management
    createEmployee,
    updateEmployee,
    deleteEmployee,
    getEmployee,
    runAutoMigration,
    
    // Permission Management
    hasPermission,
    canAccessPage,
    isCurrentUserOwner,
    
    hasFeature: (key) => {
      if (!state.companyInfo) return false;
      const f = state.companyInfo.features || {};
      if (key in f) return !!f[key];
      // safe defaults
      const defaults = {
        multi_company: false,
        backup_restore: true
      };
      return !!defaults[key];
    },
    companiesLoadAttempted: companiesLoadAttemptedRef.current
  };

  return (
    <EnhancedCompanyContext.Provider value={contextValue}>
      {children}
    </EnhancedCompanyContext.Provider>
  );
};

// Custom hook to use enhanced company context
export const useEnhancedCompany = () => {
  const context = useContext(EnhancedCompanyContext);
  if (!context) {
    throw new Error('useEnhancedCompany must be used within an EnhancedCompanyProvider');
  }
  return context;
};

export default EnhancedCompanyContext;
